// src/components/ModuleSwitcher.tsx
//
// Dropdown in the app shell header — replaces the old fixed "Dashboard Viajes"
// text. Shows the active module; clicking opens a menu of every module the
// user can see (filtered by `availableModules` in ModulesContext). Selecting
// one navigates to `/m/{key}/{defaultTab}` and persists via `rememberModule`.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useModules } from '@/context/ModulesContext';
import type { ModuleCategory, ModuleDefinition } from '@/modules';
import { defaultTabOf, resolveModule } from '@/modules';

const CATEGORY_LABELS: Record<ModuleCategory, string> = {
  ventas: 'Ventas',
  marketing: 'Marketing',
  operaciones: 'Operaciones',
  rh: 'RH',
  dev: 'Dev',
  config: 'Sistema',
  // Categorías healthcare-clinic
  consultorio: 'Consultorio',
  pacientes: 'Pacientes',
  captacion: 'Captación',
  administracion: 'Administración',
  // Categorías events-services (eventos/servicios)
  panel: 'Panel',
  clientes: 'Clientes',
  // Categoría service-pro (RentalOS)
  rentas: 'Rentas',
};

// Orden default (agency-travel, platform, etc.).
const CATEGORY_ORDER_DEFAULT: ModuleCategory[] = [
  'ventas',
  'marketing',
  'operaciones',
  'rh',
  'dev',
  'config',
];

// Orden específico para healthcare-clinic: Consultorio primero,
// Pacientes después (comunicación), Captación, Administración, Sistema.
const CATEGORY_ORDER_BY_ARCHETYPE: Record<string, ModuleCategory[]> = {
  // service-pro (renta de equipo / servicios en campo — rentalmaq): el orden
  // sigue el ciclo real del negocio. La flota va pineada arriba como home
  // del arquetipo, luego lo que cotiza y factura, luego con quién se habla,
  // luego el campo, y la captación al final.
  //
  // Antes todo caía en un solo bloque `operaciones` de nueve entradas, donde
  // Inventario y Rentas —el ERP núcleo— quedaba entre Correo y Proyectos.
  'service-pro': [
    'rentas',
    'clientes',
    'operaciones',
    'captacion',
    'rh',
    'dev',
    'config',
  ],
  'healthcare-clinic': [
    'consultorio',
    'pacientes',
    'captacion',
    'administracion',
    'operaciones',
    'dev',
    'config',
  ],
  // events-services (eventos/servicios): Panel primero, luego Clientes
  // (comunicación), Captación, Administración, Sistema.
  'events-services': [
    'panel',
    'clientes',
    'captacion',
    'administracion',
    'operaciones',
    'dev',
    'config',
  ],
};

interface ModuleSwitcherProps {
  /** URL param — module currently shown. May be undefined on first paint. */
  activeModuleKey?: string;
}

export function ModuleSwitcher({ activeModuleKey }: ModuleSwitcherProps) {
  const { availableModules, rememberModule, archetype, archetypeHomeKey } = useModules();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const active = useMemo<ModuleDefinition | undefined>(
    () => availableModules.find((m) => m.key === activeModuleKey),
    [availableModules, activeModuleKey],
  );

  // Resuelve label/categoría aplicando overrides per-archetype (si los hay).
  const resolved = useMemo(
    () => availableModules.map((m) => ({ m, r: resolveModule(m, archetype) })),
    [availableModules, archetype],
  );

  const categoryOrder = useMemo<ModuleCategory[]>(
    () => CATEGORY_ORDER_BY_ARCHETYPE[archetype ?? ''] ?? CATEGORY_ORDER_DEFAULT,
    [archetype],
  );

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return resolved;
    return resolved.filter(({ r }) => r.name.toLowerCase().includes(q));
  }, [resolved, query]);

  // Group by RESOLVED category (override per-archetype aplica).
  // El home del arquetipo se PINNEA hasta arriba en su propio grupo
  // "Principal" (pedido Samuel 2026-07-04: p.ej. Inteligencia
  // Universidades primero para el arquetipo educativo).
  const { homeEntry, grouped } = useMemo(() => {
    const home = filtered.find(({ m }) => m.key === archetypeHomeKey) ?? null;
    const map = new Map<ModuleCategory, Array<{ m: ModuleDefinition; r: { name: string; category: ModuleCategory } }>>();
    for (const entry of filtered) {
      if (home && entry.m.key === home.m.key) continue; // ya va arriba
      const cat = entry.r.category;
      const list = map.get(cat) ?? [];
      list.push(entry);
      map.set(cat, list);
    }
    const groups = categoryOrder
      .map((c) => ({ category: c, items: map.get(c) ?? [] }))
      .filter((g) => g.items.length > 0);
    return { homeEntry: home, grouped: groups };
  }, [filtered, categoryOrder, archetypeHomeKey]);

  const handleSelect = (m: ModuleDefinition) => {
    const tab = defaultTabOf(m.key) ?? '';
    rememberModule(m.key);
    navigate(tab ? `/m/${m.key}/${tab}` : `/m/${m.key}`);
    setOpen(false);
    setQuery('');
  };

  const activeResolved = active ? resolveModule(active, archetype) : null;
  const activeLabel = activeResolved?.name ?? 'Selecciona un módulo';
  const activeIcon = active?.icon ?? null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-muted/60 hover:bg-muted text-foreground transition-colors text-sm font-medium max-w-[320px]"
        title="Cambiar de módulo"
      >
        <span className="flex items-center gap-2 min-w-0">
          {typeof activeIcon === 'string' ? (
            <span className="text-base leading-none">{activeIcon}</span>
          ) : (
            <span className="shrink-0">{activeIcon}</span>
          )}
          <span className="truncate">{activeLabel}</span>
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full mt-1 w-[320px] max-h-[70vh] overflow-auto rounded-lg border border-border bg-popover text-popover-foreground shadow-xl z-50"
        >
          {availableModules.length > 6 && (
            <div className="sticky top-0 p-2 bg-popover border-b border-border">
              <label className="flex items-center gap-2 px-2 py-1 rounded-md bg-muted">
                <Search size={14} className="opacity-60" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar módulo…"
                  autoFocus
                  className="flex-1 bg-transparent outline-none text-sm"
                />
              </label>
            </div>
          )}

          {grouped.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Sin módulos que coincidan.
            </div>
          )}

          {homeEntry && (
            <div className="py-1 border-b border-border">
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Principal
              </div>
              <ModuleOption
                entry={homeEntry}
                isActive={homeEntry.m.key === activeModuleKey}
                onSelect={handleSelect}
              />
            </div>
          )}

          {grouped.map(({ category, items }) => (
            <div key={category} className="py-1">
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {CATEGORY_LABELS[category]}
              </div>
              {items.map((entry) => (
                <ModuleOption
                  key={entry.m.key}
                  entry={entry}
                  isActive={entry.m.key === activeModuleKey}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ModuleOption({
  entry,
  isActive,
  onSelect,
}: {
  entry: { m: ModuleDefinition; r: { name: string; category: ModuleCategory } };
  isActive: boolean;
  onSelect: (m: ModuleDefinition) => void;
}) {
  const { m, r } = entry;
  return (
    <button
      role="option"
      aria-selected={isActive}
      onClick={() => onSelect(m)}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
        isActive
          ? 'bg-primary/15 text-foreground'
          : 'hover:bg-muted text-foreground/90'
      }`}
    >
      <span className="shrink-0">
        {typeof m.icon === 'string' ? (
          <span className="text-base leading-none">{m.icon}</span>
        ) : (
          m.icon
        )}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block truncate">{r.name}</span>
        {m.comingSoon && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Próximamente
          </span>
        )}
      </span>
      {isActive && <span className="text-xs text-primary">✓</span>}
    </button>
  );
}

export default ModuleSwitcher;
